import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import Fuse from 'fuse.js';
import loadingGif from '../../assets/images/loader.gif';

const SearchInput = ({
  isDropdown,
  field,
  form,
  onSearch,
  disabled,
  placeholder,
  onSelect,
  minSearchLength = 3,
  debounceTime = 300,
  maxHeight = '200px',
  noResultsMessage = 'No results found',
  className = '',
  fetchAndFilter = false,
  filterKey = 'label',
  fetchTrigger = null,
  onTextChange,
}) => {
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [allResults, setAllResults] = useState([]);
  const [lastSelectedValue, setLastSelectedValue] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    setLastSelectedValue(field.value);
  }, [field.value]);

  // Initial fetch of data if fetchAndFilter is true
  useEffect(() => {
    const fetchInitialData = async () => {
      if (fetchTrigger && fetchAndFilter) {
        try {
          // setShowDropdown(true);
          setIsLoading(true);
          const results = await onSearch(''); // Empty search term to fetch all results
          const formattedResults = Array.isArray(results) ? results : [];
          setAllResults(formattedResults);
          setSearchResults(formattedResults);
        } catch (error) {
          console.error('Initial data fetch failed:', error);
          setAllResults([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchInitialData();
  }, [fetchAndFilter, fetchTrigger]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Configure Fuse.js
  const fuse = new Fuse(allResults, {
    keys: [filterKey], // Search by specified key
    threshold: 0.4, // Balance between strict & fuzzy (0.0 = exact, 1.0 = very loose)
    includeScore: true, // Helps in sorting based on match confidence
    ignoreLocation: true, // Makes matching more flexible across words
    shouldSort: true, // Ensures Fuse sorts by best match
    distance: 100, // Controls word proximity allowance
    findAllMatches: true, // Ensures it finds ALL exact words
  });

  // Filtering function
  const filterLocalResults = (searchTerm) => {
    // Normalize the search term by removing extra spaces
    const normalizedSearchTerm = searchTerm
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    if (!searchTerm) {
      setSearchResults(allResults);
      return;
    }

    const results = fuse.search(normalizedSearchTerm).map(({ item }) => item);
    setSearchResults(results);
    setShowDropdown(true);
  };

  const debouncedSearch = useCallback(
    debounce(async (searchTerm) => {
      if (fetchAndFilter) {
        filterLocalResults(searchTerm);
      } else {
        if (!searchTerm || searchTerm.length < minSearchLength) {
          setSearchResults([]);
          return;
        }

        try {
          setIsLoading(true);
          setShowDropdown(true);

          const results = await onSearch(searchTerm);
          setSearchResults(Array.isArray(results) ? results : []);
        } catch (error) {
          console.error('Search failed:', error);
          setSearchResults([]);
        } finally {
          setIsLoading(false);
        }
      }
    }, debounceTime),
    [onSearch, minSearchLength, fetchAndFilter, allResults, filterKey],
  );

  const handleInputChange = (e) => {
    if (isDropdown) {
      return;
    }
    const value = e.target.value;
    form.setFieldValue(field.name, value);
    if (value.length > 0) {
      setShowDropdown(true);
    }

    // If the value has changed from the last selected value,
    // trigger the onTextChange callback
    if (value !== lastSelectedValue && onTextChange) {
      onTextChange(field.name);
    }

    debouncedSearch(value);
  };

  const handleFocus = () => {
    if (isDropdown) {
      if (disabled) {
        return;
      }
      setShowDropdown(true);
      return;
    }
    if (lastSelectedValue.length > 0) {
      if (fetchTrigger) {
        debouncedSearch(lastSelectedValue);
      }
      setShowDropdown(true);
    }
  };

  const handleSelect = async (result) => {
    setLastSelectedValue(result.label);

    // First, call onSelect which will trigger setFieldValue in the parent
    await Promise.resolve(onSelect(result));

    // Then, after a micro-task delay to ensure field value is set
    setTimeout(() => {
      form.setFieldTouched(field.name, true);
      const idFieldName = `${field.name}Id`;
      if (form.getFieldMeta && form.getFieldMeta(idFieldName)) {
        form.setFieldTouched(idFieldName, true);
      }
      // Force Formik to validate
      form.validateForm();
    }, 0);

    setShowDropdown(false);
  };

  const showError = form.errors[field.name] && form.touched[field.name];

  return (
    <div className="position-relative" ref={wrapperRef}>
      <div className="input-group">
        {isDropdown ? (
          <div
            className={`form-control ${className} ${disabled ? 'disabled pe-none' : ''} ${
              showError ? 'is-invalid' : ''
            } d-flex align-items-center`}
            onClick={handleFocus}
            style={{
              cursor: disabled ? '' : 'pointer',
              background: disabled ? '#E9ECEF' : '',
            }}
            aria-expanded={showDropdown}
          >
            {field.value || (
              <span
                style={{
                  color: '#8692A6',
                  fontWeight: 500,
                }}
              >
                {placeholder}
              </span>
            )}
          </div>
        ) : (
          <input
            {...field}
            type="text"
            className={`form-control ${className} ${showError ? 'is-invalid' : ''}`}
            onChange={handleInputChange}
            autoComplete="off"
            placeholder={placeholder}
            disabled={disabled}
            onFocus={handleFocus}
            onClick={handleFocus}
            aria-expanded={showDropdown}
          />
        )}
      </div>

      {showDropdown && (fetchAndFilter || field.value || isLoading) && (
        <div
          className="dropdown-menu d-block position-absolute start-0 end-0 mt-1"
          style={{ zIndex: 1000 }}
        >
          {isLoading ? (
            <div className="dropdown-item m-0 p-0">
              <span className="text-primary">
                <img
                  style={{
                    maxHeight: '80px',
                    height: '60px',
                    display: 'block',
                    margin: '0 auto',
                  }}
                  src={loadingGif}
                  alt="Loading..."
                  className="loader-gif"
                />
              </span>
            </div>
          ) : searchResults.length > 0 ? (
            <div style={{ maxHeight, overflowY: 'auto', whiteSpace: 'normal' }}>
              {searchResults.map((result, index) => (
                <button
                  key={index}
                  type="button"
                  className="dropdown-item"
                  onClick={() => handleSelect(result)}
                  onMouseDown={(e) => e.preventDefault()} // Prevent dropdown from closing immediately
                  style={{ whiteSpace: 'normal' }}
                >
                  {result.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="dropdown-item text-muted text-center fst-italic">
              {noResultsMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

SearchInput.propTypes = {
  isDropdown: PropTypes.bool,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
  }).isRequired,
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    getFieldMeta: PropTypes.func.isRequired,
    setFieldTouched: PropTypes.func.isRequired,
    validateForm: PropTypes.func.isRequired,
  }).isRequired,
  onSearch: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  minSearchLength: PropTypes.number,
  debounceTime: PropTypes.number,
  maxHeight: PropTypes.string,
  noResultsMessage: PropTypes.string,
  className: PropTypes.string,
  fetchAndFilter: PropTypes.bool,
  filterKey: PropTypes.string,
  fetchTrigger: PropTypes.any,
  onTextChange: PropTypes.func.isRequired,
};

export default SearchInput;
